import React, { useMemo, useState } from 'react';
import axios from 'axios';
import Button from '../../components/Button';
import {
    Actions,
    Card,
    Container,
    Divider,
    ErrorText,
    Field,
    Helper,
    Input,
    Label,
    Row,
    Select,
    Title,
    ToggleButton,
    ToggleWrap,
} from './TestDeviceStyles';

type Environment = 'PRD' | 'DEV';
type DeviceType = 'VACUUM' | 'WASH' | 'LAUNDRY';

const TestDevice: React.FC = () => {
    const urlPrd = 'https://api.redezandona.com.br/api/v1';
    const urlDev = 'https://core-staging.zandona.elemental.kubescript.io/api/v1';

    const vacuumcleanerSufix = '/vacuumcleaner/publish';
    const laundrySufix = '/laundry-machines/publish';
    const washSufix = '/machine/publish';

    // UI state
    const [env, setEnv] = useState<Environment>('PRD');
    const [deviceType, setDeviceType] = useState<DeviceType>('VACUUM');

    const [stationCode, setStationCode] = useState('');
    const [deviceId, setDeviceId] = useState('');

    // Dynamic fields
    const [timeOn, setTimeOn] = useState<string>(''); // vacuum
    const [washingType, setWashingType] = useState<'L1' | 'L2' | 'L3' | 'L4'>('L1'); // wash

    const [laundryType, setLaundryType] = useState<'L' | 'S'>('L'); // laundry
    const [pulse, setPulse] = useState<string>('');
    const [pulseDuration, setPulseDuration] = useState<string>('');
    const [pulseInterval, setPulseInterval] = useState<string>('');

    const [loading, setLoading] = useState(false);
    const [errorMsg, setErrorMsg] = useState<string>('');
    const [successMsg, setSuccessMsg] = useState<string>('');

    const baseUrl = useMemo(() => (env === 'PRD' ? urlPrd : urlDev), [env]);

    const url = useMemo(() => {
        if (deviceType === 'VACUUM') return baseUrl + vacuumcleanerSufix;
        if (deviceType === 'WASH') return baseUrl + washSufix;
        return baseUrl + laundrySufix;
    }, [baseUrl, deviceType]);

    const buildPayload = () => {
        if (deviceType === 'VACUUM') {
            return {
                id: deviceId,
                status: 'on',
                time_on: Number(timeOn),
                stationCode,
            };
        }

        if (deviceType === 'WASH') {
            return {
                id: deviceId,
                washingType,
                stationCode,
            };
        }

        return {
            deviceId,
            type: laundryType,
            pulse: Number(pulse),
            pulseDuration: Number(pulseDuration),
            pulseInterval: Number(pulseInterval),
        };
    };

    const validate = (): string | null => {
        if (!deviceId.trim()) return 'DeviceId é obrigatório.';
        if (!stationCode.trim()) return 'Posto (stationCode) é obrigatório.';

        if (deviceType === 'VACUUM') {
            if (timeOn.trim() === '') return 'Tempo ligado (segundos) é obrigatório.';
            if (Number.isNaN(Number(timeOn)) || Number(timeOn) <= 0) return 'Tempo ligado precisa ser um número > 0.';
        }

        if (deviceType === 'LAUNDRY') {
            const nPulse = Number(pulse);
            const nDur = Number(pulseDuration);
            const nInt = Number(pulseInterval);

            if (pulse.trim() === '' || pulseDuration.trim() === '' || pulseInterval.trim() === '') {
                return 'Pulse, pulseDuration e pulseInterval são obrigatórios.';
            }
            if ([nPulse, nDur, nInt].some((n) => Number.isNaN(n) || n < 0)) {
                return 'Pulse / Duração / Intervalo precisam ser números válidos (>= 0).';
            }
        }

        return null;
    };

    const handleClick = async () => {
        setErrorMsg('');
        setSuccessMsg('');

        const validationError = validate();
        if (validationError) {
            setErrorMsg(validationError);
            return;
        }

        const payload = buildPayload();

        try {
            setLoading(true);
            const response = await axios.post(url, payload);

            console.log('Requisição bem-sucedida', { url, payload, response: response.data });
            setSuccessMsg('Enviado com sucesso!');
        } catch (error) {
            console.error('Erro ao enviar comando', error);
            setErrorMsg('Erro ao enviar comando. Veja o console para detalhes.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Container>
            <Card>
                <Title>Testar Dispositivo</Title>

                <Row>
                    <Field>
                        <Label>Ambiente</Label>
                        <ToggleWrap>
                            <ToggleButton type="button" $active={env === 'PRD'} onClick={() => setEnv('PRD')}>
                                PRD
                            </ToggleButton>
                            <ToggleButton type="button" $active={env === 'DEV'} onClick={() => setEnv('DEV')}>
                                DEV
                            </ToggleButton>
                        </ToggleWrap>
                    </Field>

                    <Field>
                        <Label>Tipo</Label>
                        <Select value={deviceType} onChange={(e) => setDeviceType(e.target.value as DeviceType)}>
                            <option value="VACUUM">Aspirador</option>
                            <option value="WASH">Lavação</option>
                            <option value="LAUNDRY">Lavanderia</option>
                        </Select>
                    </Field>
                </Row>

                <Divider />

                <Row>
                    <Field>
                        <Label>Posto</Label>
                        <Input value={stationCode} onChange={(e) => setStationCode(e.target.value)} placeholder="Ex: Z7" />
                    </Field>

                    <Field>
                        <Label>DeviceId</Label>
                        <Input value={deviceId} onChange={(e) => setDeviceId(e.target.value)} placeholder="Ex: z7asp1" />
                    </Field>
                </Row>

                <Divider />

                {/* Dynamic fields */}
                {deviceType === 'VACUUM' && (
                    <Row>
                        <Field>
                            <Label>Tempo ligado (segundos)</Label>
                            <Input
                                value={timeOn}
                                onChange={(e) => setTimeOn(e.target.value)}
                                placeholder="Ex: 30"
                                inputMode="numeric"
                            />
                        </Field>
                        <div />
                    </Row>
                )}

                {deviceType === 'WASH' && (
                    <Row>
                        <Field>
                            <Label>Tipo de lavação</Label>
                            <Select value={washingType} onChange={(e) => setWashingType(e.target.value as any)}>
                                <option value="L1">L1</option>
                                <option value="L2">L2</option>
                                <option value="L3">L3</option>
                                <option value="L4">L4</option>
                            </Select>
                        </Field>
                        <div />
                    </Row>
                )}

                {deviceType === 'LAUNDRY' && (
                    <>
                        <Row>
                            <Field>
                                <Label>Tipo de lavanderia</Label>
                                <Select value={laundryType} onChange={(e) => setLaundryType(e.target.value as 'L' | 'S')}>
                                    <option value="L">Lavar (L)</option>
                                    <option value="S">Secar (S)</option>
                                </Select>
                            </Field>
                            <div />
                        </Row>

                        <Divider />

                        <Row>
                            <Field>
                                <Label>Quantidade de pulsos</Label>
                                <Input value={pulse} onChange={(e) => setPulse(e.target.value)} placeholder="Ex: 5" inputMode="numeric" />
                            </Field>

                            <Field>
                                <Label>Duração do pulso</Label>
                                <Input
                                    value={pulseDuration}
                                    onChange={(e) => setPulseDuration(e.target.value)}
                                    placeholder="Ex: 300"
                                    inputMode="numeric"
                                />
                            </Field>
                        </Row>

                        <Row style={{ marginTop: 12 }}>
                            <Field>
                                <Label>Intervalo entre os pulsos</Label>
                                <Input
                                    value={pulseInterval}
                                    onChange={(e) => setPulseInterval(e.target.value)}
                                    placeholder="Ex: 200"
                                    inputMode="numeric"
                                />
                            </Field>
                        </Row>
                    </>
                )}

                <Helper>
                    <b>URL:</b> {url}
                    <br />
                    <b>Payload:</b> {JSON.stringify(buildPayload(), null, 2)}
                </Helper>

                {!!errorMsg && <ErrorText>{errorMsg}</ErrorText>}
                {!!successMsg && <Helper>{successMsg}</Helper>}

                <Actions>
                    <Button title={loading ? 'Enviando...' : 'Testar'} onClick={handleClick} disabled={loading} />
                </Actions>
            </Card>
        </Container>
    );
};

export default TestDevice;
